import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Coffee, Truck, ShoppingBag, Scissors, Dumbbell, Store, ArrowLeft, ShieldCheck, Loader2, PackageCheck, Wrench, Leaf, Wind } from "lucide-react";
import { useOperator } from "@/context/OperatorContext";

const CONNECT_TYPES = ["realestate", "coaching", "consulting"];

const PRESETS = [
  { key: "cafe", label: "Café / Restaurant", desc: "Menu ordering with pickup & prep timer", icon: Coffee, modes: ["ordering"], cart_type: "food", available: true },
  { key: "food_truck", label: "Food truck", desc: "On-the-go ordering for a mobile spot", icon: Truck, modes: ["ordering"], cart_type: "food", available: true },
  { key: "retail", label: "Retail / goods", desc: "Sell products for pickup", icon: ShoppingBag, modes: ["ordering"], cart_type: "food", available: true },
  { key: "barber", label: "Barber / Salon", desc: "Appointment booking with staff & time slots", icon: Scissors, modes: ["booking"], cart_type: "barber", available: true },
  { key: "realestate", label: "Real Estate / Agents", desc: "Agent directory, profiles & lead routing (MorrConnect)", icon: Store, modes: ["booking"], cart_type: "realestate", available: true },
  { key: "coaching", label: "Coaching & Fitness", desc: "Your profile, sessions, packages & bookings (MorrConnect)", icon: Dumbbell, modes: ["booking"], cart_type: "coaching", available: true },
  { key: "consulting", label: "Consulting", desc: "Profile, discovery calls & leads (MorrConnect)", icon: Store, modes: ["booking"], cart_type: "consulting", available: true },
  { key: "handyman", label: "Handyman", desc: "Quote requests, online approval & scheduled visits", icon: Wrench, modes: ["booking"], cart_type: "handyman", available: true },
  { key: "landscaper", label: "Landscaping", desc: "Quote requests, online approval & scheduled visits", icon: Leaf, modes: ["booking"], cart_type: "landscaper", available: true },
  { key: "hvac", label: "HVAC", desc: "Service calls: request, quote & technician visit", icon: Wind, modes: ["booking"], cart_type: "hvac", available: true },
  { key: "trainer", label: "Trainer / Coach", desc: "Session booking, packages & memberships", icon: Dumbbell, modes: ["booking"], cart_type: "coaching", available: false },
  { key: "other", label: "Something else", desc: "Start with ordering; add more later", icon: Store, modes: ["ordering"], cart_type: "food", available: true },
];

export function OnboardingModal({ open, onComplete, onCancel }) {
  const connectApp = useOperator()?.operator?.app === "morrconnect";
  const [step, setStep] = useState(1);
  const [sel, setSel] = useState(null);
  const [docs, setDocs] = useState(null);
  const [tos, setTos] = useState(false);
  const [dpa, setDpa] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const chosen = PRESETS.find((p) => p.key === sel);

  useEffect(() => {
    if (open && !docs) {
      opApi.get("/tenant/legal-docs").then(({ data }) => setDocs(data)).catch(() => {});
    }
  }, [open, docs]);

  useEffect(() => {
    if (open && catalog === null) {
      opApi.get("/tenant/catalog/health").then(({ data }) => setCatalog(data)).catch(() => setCatalog({}));
    }
  }, [open, catalog]);

  const finish = async () => {
    if (!chosen || !tos || !dpa) return;
    setSubmitting(true);
    try {
      await opApi.post("/tenant/legal/accept", { version: docs?.version, tos: true, dpa: true });
      await onComplete(chosen.key, chosen.modes, chosen.cart_type || "food");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="rounded-none sm:max-w-2xl" data-testid="onboarding-modal">
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Welcome to {connectApp ? "MorrConnect" : "MorrCart"}</DialogTitle>
              <DialogDescription>
                What kind of business do you run? This sets up your dashboard. You can change it later in Settings.
              </DialogDescription>
            </DialogHeader>
            {catalog?.has_catalog && catalog?.count > 0 && (
              <div className="flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-800 px-3 py-2 text-sm mt-1" data-testid="onboarding-catalog-ready">
                <PackageCheck className="h-4 w-4 flex-none text-emerald-600" />
                <span><strong>{catalog.count} product{catalog.count === 1 ? "" : "s"} ready to sync</strong> — we'll add them to your store automatically.</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {PRESETS.filter((p) => !connectApp || (p.available && CONNECT_TYPES.includes(p.cart_type))).map((p) => {
                const Icon = p.icon;
                const active = sel === p.key;
                return (
                  <button
                    key={p.key}
                    disabled={!p.available}
                    onClick={() => p.available && setSel(p.key)}
                    data-testid={`onboarding-type-${p.key}`}
                    className={`text-left border p-4 transition-colors ${active ? "border-primary bg-primary/5" : "border-border"} ${!p.available ? "opacity-50 cursor-not-allowed" : "hover:border-primary/60"}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5 text-primary" />
                      <span className="font-semibold text-sm">{p.label}</span>
                      {!p.available && <span className="ml-auto text-[10px] uppercase font-bold bg-secondary px-1.5 py-0.5">Coming soon</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">{p.desc}</p>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between items-center mt-4">
              <Button variant="ghost" className="rounded-none" onClick={onCancel} data-testid="onboarding-cancel">
                Cancel
              </Button>
              <Button className="rounded-none" disabled={!chosen} data-testid="onboarding-continue" onClick={() => setStep(2)}>
                Continue
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <ShieldCheck className="h-6 w-6 text-primary" /> Review &amp; accept your terms
              </DialogTitle>
              <DialogDescription>
                Before your cart goes live, please review the platform terms. MorrCart processes customer data on your behalf — you remain the data controller.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-2 max-h-[46vh] overflow-y-auto pr-1" data-testid="onboarding-legal-scroll">
              <div className="border border-border p-4 text-sm leading-relaxed text-muted-foreground [&_h4]:text-foreground [&_h4]:font-display [&_h4]:text-base [&_h4]:mb-2 [&_p]:mb-2 [&_strong]:text-foreground"
                   dangerouslySetInnerHTML={{ __html: docs?.tos_html || "Loading…" }} />
              <div className="border border-border p-4 text-sm leading-relaxed text-muted-foreground [&_h4]:text-foreground [&_h4]:font-display [&_h4]:text-base [&_h4]:mb-2 [&_p]:mb-2 [&_strong]:text-foreground [&_em]:text-foreground"
                   dangerouslySetInnerHTML={{ __html: docs?.dpa_html || "Loading…" }} />
            </div>

            <div className="space-y-3 mt-3">
              <label className="flex items-start gap-3 cursor-pointer text-sm">
                <Checkbox checked={tos} onCheckedChange={(v) => setTos(!!v)} className="mt-0.5 rounded-none" data-testid="onboarding-accept-tos" />
                <span>I have read and agree to the <strong>MorrCart Platform Terms of Service</strong>.</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer text-sm">
                <Checkbox checked={dpa} onCheckedChange={(v) => setDpa(!!v)} className="mt-0.5 rounded-none" data-testid="onboarding-accept-dpa" />
                <span>I agree to the <strong>Data Processing Addendum</strong> and confirm I am authorized to accept on behalf of my business.</span>
              </label>
            </div>

            <div className="flex justify-between items-center mt-4">
              <Button variant="ghost" className="rounded-none gap-1" onClick={() => setStep(1)} data-testid="onboarding-back">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" className="rounded-none" onClick={onCancel} data-testid="onboarding-cancel-step2">
                  Cancel
                </Button>
                <Button className="rounded-none" disabled={!tos || !dpa || submitting} data-testid="onboarding-finish" onClick={finish}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Accept &amp; finish
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
